import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';

const SDK_SELECT_SELECTOR = '.select-layout-v2';
const SDK_CONTROL_SELECTOR = '.generic-select__control';
const SDK_OPTION_SELECTOR = '.generic-select__option';
const SDK_VALUE_SELECTOR = '.generic-select__single-value';
const TEMPLATE_SOURCE_OPTIONS = [
  { id: '1', title: 'My Templates' },
  { id: '2', title: 'Team Templates' },
  { id: '3', title: 'OLC Templates' },
];

const dispatchMouseDown = (element) => {
  element?.dispatchEvent(
    new MouseEvent('mousedown', {
      bubbles: true,
      button: 0,
      cancelable: true,
      view: window,
    })
  );
};

const waitForSdkMenu = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const openSdkMenu = async (sdkSelect) => {
  const input = sdkSelect?.querySelector('input');

  if (input?.getAttribute('aria-expanded') !== 'true') {
    dispatchMouseDown(sdkSelect?.querySelector(SDK_CONTROL_SELECTOR));
    await waitForSdkMenu();
  }
};

const closeSdkMenu = (sdkSelect) => {
  const input = sdkSelect?.querySelector('input');

  if (input?.getAttribute('aria-expanded') === 'true') {
    // The gallery dialog also listens for Escape on document. Blurring closes
    // react-select without leaking a modal-level keyboard event.
    input.blur();
  }
};

const getSdkSelectedLabel = (sdkSelect) =>
  sdkSelect?.querySelector(SDK_VALUE_SELECTOR)?.textContent?.trim() || '';

const getSdkOptions = (sdkSelect) => {
  const input = sdkSelect?.querySelector('input');
  const listbox = document.getElementById(input?.getAttribute('aria-controls'));
  const optionElements = listbox
    ? [...listbox.querySelectorAll(SDK_OPTION_SELECTOR)]
    : [...sdkSelect.querySelectorAll(SDK_OPTION_SELECTOR)];

  return optionElements;
};

const waitForSdkOptions = async (sdkSelect, remainingAttempts = 10) => {
  const options = getSdkOptions(sdkSelect);
  if (options.length || remainingAttempts <= 0) return options;

  await waitForSdkMenu();
  return waitForSdkOptions(sdkSelect, remainingAttempts - 1);
};

const normalizeForMatch = (text) =>
  String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const selectSdkOption = async (sdkSelect, title) => {
  await openSdkMenu(sdkSelect);

  const normalizedTitle = normalizeForMatch(title);
  const option = (await waitForSdkOptions(sdkSelect)).find(
    (candidate) => normalizeForMatch(candidate.textContent) === normalizedTitle
  );

  if (!option) {
    closeSdkMenu(sdkSelect);
    return false;
  }

  option.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      button: 0,
      cancelable: true,
      view: window,
    })
  );
  return true;
};

const SdkDropdownAdapter = ({ sdkSelect }) => {
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    const syncSelectedValue = () => {
      const selectedLabel = getSdkSelectedLabel(sdkSelect);

      setSelectedValue((currentValue) => {
        if (!selectedLabel) return null;
        if (currentValue?.title === selectedLabel) return currentValue;

        return { id: selectedLabel, title: selectedLabel };
      });
    };

    syncSelectedValue();

    const observer = new MutationObserver(syncSelectedValue);
    observer.observe(sdkSelect, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      closeSdkMenu(sdkSelect);
    };
  }, [sdkSelect]);

  const handleSelection = async (option) => {
    if (!option) return;

    sdkSelect
      .closest('.filter-select')
      ?.querySelector('.templateGalleryCentralizedDropdown [role="combobox"]')
      ?.focus();

    if (await selectSdkOption(sdkSelect, option.title)) {
      setSelectedValue(option);
    }
  };

  return (
    <OlcDropdownSearch
      className="templateGalleryCentralizedDropdown"
      menuDisableFocusTrap
      menuPaperClassName="templateGalleryCentralizedDropdownOptions templateGallerySourceDropdownOptions"
      menuZIndex={100001}
      options={TEMPLATE_SOURCE_OPTIONS}
      placeHolderText="All Templates"
      searchable={false}
      selectedValue={selectedValue}
      setSelectedValue={handleSelection}
    />
  );
};

const TemplateGalleryDropdownBridge = ({ containerId = 'template-builder-container' }) => {
  const [dropdowns, setDropdowns] = useState([]);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return undefined;

    const hosts = new Map();

    const synchronizeDropdowns = () => {
      const filters = [
        ...container.querySelectorAll('.galleryModal.modal-v2 .filters-container .filter-select'),
      ];
      const connectedFilters = new Set(filters);

      for (const [filter, entry] of hosts) {
        if (!connectedFilters.has(filter)) {
          entry.host.remove();
          hosts.delete(filter);
        }
      }

      filters.forEach((filter, index) => {
        const sdkSelect = filter.querySelector(SDK_SELECT_SELECTOR);
        if (!sdkSelect) return;

        const isCategory = filters.length > 1 && index < filters.length - 1;
        const existingEntry = hosts.get(filter);

        // Keep the SDK's original Industry/Theme dropdown. Bridging it created
        // two independently sourced category menus and made selection flicker
        // whenever their option labels did not match.
        if (isCategory) {
          if (existingEntry) {
            existingEntry.host.remove();
            hosts.delete(filter);
          }
          filter.classList.remove('hasCentralizedDropdown');
          return;
        }

        if (existingEntry) {
          if (existingEntry.host.parentElement !== filter) {
            filter.append(existingEntry.host);
          }

          if (existingEntry.sdkSelect !== sdkSelect) {
            hosts.set(filter, {
              ...existingEntry,
              sdkSelect,
            });
          }
          return;
        }

        const host = document.createElement('div');
        host.className = 'templateGalleryCentralizedDropdownHost';
        filter.classList.add('hasCentralizedDropdown');
        filter.append(host);

        hosts.set(filter, {
          host,
          sdkSelect,
        });
      });

      setDropdowns((currentDropdowns) => {
        const nextDropdowns = [...hosts.values()];
        const unchanged =
          currentDropdowns.length === nextDropdowns.length &&
          currentDropdowns.every((dropdown, index) => dropdown === nextDropdowns[index]);

        return unchanged ? currentDropdowns : nextDropdowns;
      });
    };

    synchronizeDropdowns();

    const observer = new MutationObserver(synchronizeDropdowns);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      hosts.forEach(({ host }, filter) => {
        host.remove();
        filter.classList.remove('hasCentralizedDropdown');
      });
      hosts.clear();
    };
  }, [containerId]);

  return dropdowns.map(({ host, sdkSelect }, index) =>
    createPortal(
      <SdkDropdownAdapter sdkSelect={sdkSelect} />,
      host,
      `template-source-${index}`
    )
  );
};

export default TemplateGalleryDropdownBridge;
